/**
 * This file is part of the Goobi viewer - a content presentation and management
 * application for digitized objects.
 * 
 * Visit these websites for more information.
 *          - https://goobi.io
 *          - https://www.intranda.com
 *          - https://github.com/intranda/goobi
 * 
 * This program is free software; you can redistribute it and/or modify it under the terms
 * of the GNU General Public License as published by the Free Software Foundation; either
 * version 2 of the License, or (at your option) any later version.
 * 
 * This program is distributed in the hope that it will be useful, but WITHOUT ANY
 * WARRANTY; without even the implied warranty of MERCHANTABILITY or FITNESS FOR A
 * PARTICULAR PURPOSE. See the GNU General Public License for more details.
 * 
 * You should have received a copy of the GNU General Public License along with this
 * program. If not, see <http://www.gnu.org/licenses/>.
 * 
 * Module which handles the image controls of the image view.
 * 
 * @version 3.2.0
 * @module viewImage.tools.filter
 * @requires jQuery
 */

import CoordinateConversion from "./tools/CoordinateConversion.js";
import { takeWhile } from "rxjs/operators";

const _debug = false;

const BRIGHTNESS_VALUES = {
    default: 0,
    min: -255,
    max: 255
}
const CONTRAST_VALUES = {
    default: 1,
    min: 0,
    max: 2
}
const COLOR_SATURATION_VALUES = {
    default: 1,
    min: 0,
    max: 5
}
const COLOR_ROTATION_VALUES = {
    default: 0,
    min: -180,
    max: 180
}
const BITONAL_VALUES = {
    default: 128,
    min: 0,
    max: 255
}
const BLUR_VALUES = {
    default: 3,
    min: 1,
    max: 10
}

const SHARPEN_VALUES = {
    default: 1
}

const _Pr = .299
const _Pg = .587
const _Pb = .114
 
class DrawFilter {
        
        
    constructor (viewer, filterMethod) {
        if ( _debug ) {
            console.log( '##############################' );
            console.log( 'imageView.tools.Filter.init' );
            console.log( '##############################' );
        }
        this.filterMethod = filterMethod;
        this.viewer = viewer;
        this.disabled = false;
    }
    
    start() {
        if(!this.subsription) {            
            this.subscription = this.viewer.onUpdate
            .pipe(takeWhile(() => !this.disabled))
            .subscribe((event) => {
                let context = this.viewer.getCanvasContext();
                let data = context.getImageData(0, 0, context.canvas.width, context.canvas.height);
                data = this.filterMethod(data);
                if(data) {                    
                    context.putImageData(data, 0, 0);
                }
            });
        }
        this.apply();
    }
    
    apply() {
        if(this.subscription) {            
            this.viewer.openseadragon.forceRedraw();
        }
    }

    stop() {
        this.close();
    }
    
    close() {
        if(this.subscription) {            
            this.subscription.unsubscribe();
            this.subscription = undefined;
            this.viewer.openseadragon.forceRedraw();
        } 
    }
    
   isActive() {
        return this.subscription;
    }
    

    createImageData(w,h) {
      return this.viewer.getCanvasContext().createImageData(w,h);
    };
}

export class Brightness extends DrawFilter {
    
    constructor(viewer, value) {
        if ( _debug ) {
            console.log( '##############################' );
            console.log( 'imageView.tools.Filter.Brightness.init' );
            console.log( '##############################' );
        }
        super(viewer, data => this.filter(data)); 
        this.value = value ?? BRIGHTNESS_VALUES.default;

    }
    
    setValue(value) {
        this.value = value;
        this.apply();
    }
    
    getValue() {
        return this.value;
    }

    getMinValue() {
        return BRIGHTNESS_VALUES.min;
    }

    getMaxValue() {
        return BRIGHTNESS_VALUES.max;
    }
    
    filter(pixels) {
        let adjustment = this.value;
        let data = pixels.data;
        for(var i = 0; i < data.length; i += 4) {
            var pixelData = [data[i], data[i+1], data[i+2], data[i+3]];
            data[i] = _fitRange(pixelData[0] + adjustment);
            data[i+1] = _fitRange(pixelData[1] + adjustment);
            data[i+2] = _fitRange(pixelData[2] + adjustment);
        }
        return pixels;
    }
}

export class Contrast extends DrawFilter{
    
    constructor(viewer, value) {
        if ( _debug ) {
            console.log( '##############################' );
            console.log( 'imageView.tools.Filter.Contrast.init' );
            console.log( '##############################' );
        }
        super(viewer, data => this.filter(data)); 
        this.value = value ?? CONTRAST_VALUES.default;

    }
    
    setValue(value) {
        this.value = value;
        this.apply();
    }
    
    getValue() {
        return this.value;
    }

    getMinValue() {
        return CONTRAST_VALUES.min;
    }

    getMaxValue() {
        return CONTRAST_VALUES.max;
    }
    
    
    filter(pixels) {
        let factor = this.value;
        let data = pixels.data;
        for(var i = 0; i < data.length; i += 4) {
            var pixelData = [data[i], data[i+1], data[i+2], data[i+3]];
            data[i] = _fitRange(factor * (pixelData[0] - 128) + 128);
            data[i+1] = _fitRange(factor * (pixelData[1] - 128) + 128);
            data[i+2] = _fitRange(factor * (pixelData[2] - 128) + 128);
        }
        return pixels;
    } 
}

export class ColorSaturation extends DrawFilter {
    
    constructor(viewer, value) {
        if ( _debug ) {
            console.log( '##############################' );
            console.log( 'imageView.tools.Filter.ColorSaturation.init' );
            console.log( '##############################' );
        }
        super(viewer, data => this.filter(data)); 
        this.value = value ?? COLOR_SATURATION_VALUES.default;

    }
    
    setValue (value) {
        this.value = value;
        this.apply();
    }
    
    getValue() {
        return this.value;
    }

    getMinValue() {
        return COLOR_SATURATION_VALUES.min;
    }

    getMaxValue() {
        return COLOR_SATURATION_VALUES.max;
    }
    
    
    filter(pixels) {
        let factor = this.value;
        let data = pixels.data;

        for(var i = 0; i < data.length; i += 4) {
            var pixelData = [data[i], data[i+1], data[i+2]];
            let p = _getColorVectorlength(pixelData[0], pixelData[1], pixelData[2]);
            data[i] = _fitRange(factor * (pixelData[0] - p) + p);
            data[i+1] = _fitRange(factor * (pixelData[1] - p) + p);
            data[i+2] = _fitRange(factor * (pixelData[2] - p) + p);
        }

        return pixels;
    }
}

export class ColorRotate extends DrawFilter{
    
    constructor(viewer, value) {
        if ( _debug ) {
            console.log( '##############################' );
            console.log( 'imageView.tools.Filter.Brightness.init' );
            console.log( '##############################' );
        }
        super(viewer, data => this.filter(data)); 
        this.value = value ?? COLOR_ROTATION_VALUES.default;
        this.colorRotationMatrix = _getColorRotationMatrix();
        this.backColorRotationMatrix = _getInverseColorRotationMatrix();
    }
    
    setValue(value) {
        this.value = value;
        this.apply();
    }
    
    getValue() {
        return this.value;
    }

    getMinValue() {
        return COLOR_ROTATION_VALUES.min;
    }

    getMaxValue() {
        return COLOR_ROTATION_VALUES.max;
    }
    
    filter(pixels) {
        let degree = this.value;
        let rad = degree * Math.PI / 180.0;
        let data = pixels.data;
        let matrix = _rotateColors(rad, this.colorRotationMatrix, this.backColorRotationMatrix);
        let r,g,b;
        for(let i = 0; i < data.length; i += 4) {
            r = matrix[0]*data[i] + matrix[1]*data[i+1] + matrix[2]*data[i+2] + matrix[3]*data[i+3];
            g = matrix[4]*data[i] + matrix[5]*data[i+1] + matrix[6]*data[i+2] + matrix[7]*data[i+3];
            b = matrix[8]*data[i] + matrix[9]*data[i+1] + matrix[10]*data[i+2] + matrix[11]*data[i+3];
            data[i] = r;
            data[i+1] = g;
            data[i+2] = b;
        } 
        return pixels;
    }
}

export class Invert extends DrawFilter {

    constructor(viewer) {
        if ( _debug ) {
            console.log( '##############################' );
            console.log( 'imageView.tools.Filter.Grayscale.init' );
            console.log( '##############################' );
        }
        super(viewer, data =>  this.filter(data)); 

    }

    filter(pixels) {
        let data = pixels.data;
        for (var i=0; i<data.length; i+=4) {
            data[i] = 255 - data[i];
            data[i+1] = 255 - data[i+1];
            data[i+2] = 255 - data[i+2];
          }
          return pixels;
    }
}

export class HighlightArea extends DrawFilter {
     
    constructor(viewer, shade, rect, viewerCoordinates) {
        if ( _debug ) {
            console.log( '##############################' );
            console.log( 'imageView.tools.Filter.HighlightArea.init' );
            console.log( '##############################' );
        }
        super(viewer, data => this.filter(data)); 
        this.shade = shade;
        this.setRect(rect, viewerCoordinates);
    }
    
    setRect(rect, viewerCoordinates) {
        if(!viewerCoordinates) {            
            let osRect = CoordinateConversion.convertRectFromImageToOpenSeadragon(rect, this.viewer.openseadragon, this.viewer.getOriginalImageSize());
            this.rect = osRect;
        } else {
            this.rect = rect;
        }
        this.apply();
    }
    
    getValue(viewerCoordinates) {
        if(!viewerCoordinates) {            
            let imageRect = CoordinateConversion.convertRectFromOpenSeadragonToImage(this.rect, this.viewer.openseadragon, this.viewer.getOriginalImageSize());
            return imageRect;
        } else {
            return this.rect;
        }
    }
    
    setShade(value) {
        this.shade = value;
        this.apply();
    }   

    getShade() {
        return this.shade;
    }
    
    filter(pixels) {
        let context = this.viewer.getCanvasContext();
        let canvas = context.canvas;
        
        let rects = [];
        if(Array.isArray(this.rect)) {
			this.rect.forEach( r => {
				let rect = CoordinateConversion.convertCoordinatesFromImageToCanvas(r, this.viewer.openseadragon).times(window.devicePixelRatio);
				rects.push(rect);
			});
		} else if(this.rect != undefined) {
			let rect = CoordinateConversion.convertCoordinatesFromImageToCanvas(this.rect, this.viewer.openseadragon).times(window.devicePixelRatio);
			rects.push(rect);
		}
                
        context.fillStyle = "rgba(0,0,0," + this.shade + ")";
        context.beginPath();
        
        context.moveTo(0,0);
        context.lineTo(canvas.width, 0);
        context.lineTo(canvas.width, canvas.height);
        context.lineTo(0, canvas.height);
        context.lineTo(0, 0);
//        context.closePath();
//        
//        context.beginPath();
		rects.forEach(rect => {			
	        context.moveTo(rect.x, rect.y);
	        context.lineTo(rect.x, rect.y + rect.height);
	        context.lineTo(rect.x + rect.width, rect.y + rect.height);
	        context.lineTo(rect.x + rect.width, rect.y);
	        context.moveTo(rect.x, rect.y);
		});
        context.closePath();
        
        context.fill();
    }
 }

export class Grayscale extends DrawFilter {
    
    constructor(viewer) {
        if ( _debug ) {
            console.log( '##############################' );
            console.log( 'imageView.tools.Filter.Grayscale.init' );
            console.log( '##############################' );
        }
        super(viewer, data => this.filter(data)); 
    }
        
    filter(pixels) {
        let data = pixels.data;
        for (var i=0; i<data.length; i+=4) {
            var r = data[i];
            var g = data[i+1];
            var b = data[i+2];
            // CIE luminance for the RGB
            // The human eye is bad at seeing red and blue, so we de-emphasize them.
            var v = 0.2126*r + 0.7152*g + 0.0722*b;
            data[i] = data[i+1] = data[i+2] = v
          }
          return pixels;
    }
}

export class Threshold extends DrawFilter {
    
    constructor(viewer, value) {
        if ( _debug ) {
            console.log( '##############################' );
            console.log( 'imageView.tools.Filter.Threshold.init' );
            console.log( '##############################' );
        }
        super(viewer, data => this.filter(data)); 
        this.value = value ?? BITONAL_VALUES.default;
    }

    setValue(value) {
        this.value = value;
        this.apply();
    }
    
    getValue() {
        return this.value;
    }

    getMinValue() {
        return BITONAL_VALUES.min;
    }

    getMaxValue() {
        return BITONAL_VALUES.max;
    }
    
    filter(pixels) {
        let threshold = this.value;
        let data = pixels.data;
        for(var i = 0; i < data.length; i += 4) {
            var r = data[i];
            var g = data[i+1];
            var b = data[i+2];
            var v = (0.2126*r + 0.7152*g + 0.0722*b >= threshold) ? 255 : 0;
            data[i] = data[i+1] = data[i+2] = v;
        }
        return pixels;
    }
}

class ConvolutionFilter extends DrawFilter {

  constructor(viewer, filterMatrix, opaque) {
    if ( _debug ) {
        console.log( '##############################' );
        console.log( 'imageView.tools.ConvolutionFilter.init' );
        console.log( '##############################' );
    }
    super(viewer, data => this.filter(data)); 
    this.matrix = filterMatrix;
    this.opaque = opaque;
}
    
filter(pixels) {
    return this.convolute(pixels, this.getMatrix(), this.opaque);
}

getMatrix() {
    return this.matrix;
}

convolute (pixels, weights, opaque) {
    var side = Math.round(Math.sqrt(weights.length));
    var halfSide = Math.floor(side/2);
    var src = pixels.data;
    var sw = pixels.width;
    var sh = pixels.height;
    // pad output by the convolution matrix
    var w = sw;
    var h = sh;
    var output = this.createImageData(w, h);
    var dst = output.data;
    // go through the destination image pixels
    var alphaFac = opaque ? 1 : 0;
    for (var y=0; y<h; y++) {
      for (var x=0; x<w; x++) {
        var sy = y;
        var sx = x;
        var dstOff = (y*w+x)*4;
        // calculate the weighed sum of the source image pixels that
        // fall under the convolution matrix
        var r=0, g=0, b=0, a=0;
        for (var cy=0; cy<side; cy++) {
          for (var cx=0; cx<side; cx++) {
            var scy = sy + cy - halfSide;
            var scx = sx + cx - halfSide;
            if (scy >= 0 && scy < sh && scx >= 0 && scx < sw) {
              var srcOff = (scy*sw+scx)*4;
              var wt = weights[cy*side+cx];
              r += src[srcOff] * wt;
              g += src[srcOff+1] * wt;
              b += src[srcOff+2] * wt;
              a += src[srcOff+3] * wt;
            }
          }
        }
        dst[dstOff] = r;
        dst[dstOff+1] = g;
        dst[dstOff+2] = b;
        dst[dstOff+3] = a + alphaFac*(255-a);
      }
    }
    return output;
  };
}

export class Sharpen extends ConvolutionFilter {
        
        constructor(viewer, value) {
        if ( _debug ) {
            console.log( '##############################' );
            console.log( 'imageView.tools.Filter.Sharpen.init' );
            console.log( '##############################' );
        }
        super(viewer, undefined, false);
        this.value = value ?? SHARPEN_VALUES.default;

    }

    setValue(value) {
        this.value = value;
        this.apply();
    }
    
    getValue() {
        return this.value;
    }

    
    getMatrix() {
        let factor = this.value;
        let matrix = [  0, -factor,  0,
            -factor,  5*factor, -factor,
            0, -factor,  0 ];
        return matrix;
    } 
}

export class Blur extends ConvolutionFilter {

    constructor(viewer, value) {
        if ( _debug ) {
            console.log( '##############################' );
            console.log( 'imageView.tools.Filter.Blur.init' );
            console.log( '##############################' );
        }
        super(viewer, undefined, false); 
        this.value = value ?? BLUR_VALUES.default;

    }
    
    setValue(value) {
            this.value = value;
            this.apply();
    }
    
    getValue() {
        return this.value;
    }
    
    getMinValue() {
        return BLUR_VALUES.min;
    }

    getMaxValue() {
        return BLUR_VALUES.max;
    }
    
    getMatrix() {
        let size = this.value;
        let matrix = [];
        let value = 1/(size*size);
        for ( var i = 0; i < size*size; i++ ) {
            matrix.push(value);
        }
        return matrix;
    } 
}

function _rotateColors(rad, matrix, inverseMatrix) {
    let cos = Math.cos(rad);
    let sin = Math.sin(rad);
    let zrot = [
        cos,-sin,   0,  0,
        sin, cos,   0,  0,
            0,   0,   1,  0,
            0,   0,   0,  1
        ]
    let mat = _multiplyMatrices(zrot, matrix);
    mat = _multiplyMatrices(inverseMatrix, mat);
    
    return mat;
}

function _getColorRotationMatrix() {
    let xrot = [
        1,   0,   0,  0,
        0, 1/Math.sqrt(2),-1/Math.sqrt(2),  0,
        0, 1/Math.sqrt(2), 1/Math.sqrt(2),  0,
        0,   0,   0,  1
    ];
    let yrot = [
        Math.sqrt(2/3),   0, -1/Math.sqrt(3),  0,
        0,   1,   0,  0,
        1/Math.sqrt(3),   0, Math.sqrt(2/3),  0,
        0,   0,   0,  1
    ];
    let mat = _multiplyMatrices(yrot, xrot);  
    return mat;
}

function _getInverseColorRotationMatrix() {
    let xrotInv = [
        1,   0,   0,  0,
        0, 1/Math.sqrt(2), 1/Math.sqrt(2),  0,
        0, -1/Math.sqrt(2), 1/Math.sqrt(2),  0,
        0,   0,   0,  1
    ];
    let yrotInv = [
        Math.sqrt(2/3),   0, 1/Math.sqrt(3),  0,
        0,   1,   0,  0,
        -1/Math.sqrt(3),   0, Math.sqrt(2/3),  0,
        0,   0,   0,  1
    ];
    let mat = _multiplyMatrices(xrotInv, yrotInv);  
    return mat;
}

function _multiplyMatrices(m1, m2) {
    let rows = Math.sqrt(m1.length);
    var result = new Array(m1.length);
    for (var i = 0; i < rows; i++) {
        for (var j = 0; j < rows; j++) {
            var sum = 0;
            for (var k = 0; k < rows; k++) {
                sum += m1[i*rows+k] * m2[k*rows+j];
            }
            result[i*rows+j] = sum;
        }
    }
    return result;
}

function _fitRange(value) {
    return value > 255 ? 255 : (value < 0 ? 0 : value);
}

function _getColorVectorlength(r,g,b) {
    let l = Math.sqrt(r*r*_Pr + g*g*_Pg + b*b*_Pb);
    return l;
}
